"use client";
import React, { useState, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Star,
  Store,
  ShoppingCart,
  X,
  ThumbsUp,
  MessageCircle,
  User,
  Loader2,
  Image,
  Heart,
} from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import {
  Autoplay,
  Navigation,
  Pagination as SwiperPagination,
} from "swiper/modules";
import { motion, AnimatePresence } from "framer-motion";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import Link from "next/link";
import useCurrency from "@/hooks/useCurrency";
import { useAuth } from "@/context/AuthProvider";
import toast from "react-hot-toast";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useWishlist } from "@/context/WishlistProvider";

// Default Image Component
const DefaultImage = ({ className }) => (
  <div className={`flex items-center justify-center bg-gray-100 ${className}`}>
    <Image className="w-1/3 h-1/3 text-gray-400" />
  </div>
);

const LoadingSpinner = () => (
  <motion.div
    animate={{ rotate: 360 }}
    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
  >
    <Loader2 className="w-5 h-5" />
  </motion.div>
);

const CommentModal = ({ isOpen, onClose, productId }) => {
  const [comment, setComment] = useState("");
  const { getToken } = useAuth();
  const token = getToken();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error(t("alerts.Login_required"));
      return;
    }
    if (!comment.trim()) {
      toast.error(t("commentSection.commentEmpty"));
      return;
    }

    try {
      setLoading(true);
      await axios.post(
        `${process.env.NEXT_PUBLIC_BASE_URL}/api/leave-comment`,
        {
          comment,
          id_product: productId,
        },
        {
          headers: {
            token,
          },
        }
      );
      setComment("");
      toast.success(t("commentSection.commentAdded"));
      window.location.reload();
      onClose();
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error(t("commentSection.error"));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="bg-white rounded-lg p-6 w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">
                {t("commentSection.addReview")}
              </h3>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={onClose}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <X className="w-6 h-6" />
              </motion.button>
            </div>

            <form onSubmit={handleAddComment} className="space-y-4">
              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <label className="block text-sm font-medium mb-1">
                  {t("commentSection.yourReview")}
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className="w-full p-2 border rounded-lg h-32 resize-none outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                  required
                  disabled={loading}
                  placeholder={t("commentSection.writePlaceholder")}
                />
              </motion.div>

              <motion.button
                type="submit"
                whileHover={!loading ? { scale: 1.02 } : {}}
                whileTap={!loading ? { scale: 0.98 } : {}}
                disabled={loading}
                className={`w-full py-2 px-4 rounded-lg text-white font-medium flex items-center justify-center gap-2 transition-all duration-200 ${
                  loading
                    ? "bg-emerald-400 cursor-not-allowed"
                    : "bg-emerald-500 hover:bg-emerald-600"
                }`}
              >
                {loading ? (
                  <>
                    <LoadingSpinner />
                    <span>{t("commentSection.submitting")}</span>
                  </>
                ) : (
                  <span>{t("commentSection.submitReview")}</span>
                )}
              </motion.button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Comment = ({ comment }) => {
  const { t } = useTranslation();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="border-b border-gray-200 py-4"
    >
      <div className="flex items-start gap-3">
        <motion.div
          whileHover={{ scale: 1.1 }}
          className="bg-gray-100 rounded-full p-2"
        >
          <User className="w-6 h-6 text-gray-600" />
        </motion.div>
        <div className="flex-1">
          <div className="flex justify-between items-start">
            <div>
              <h4 className="font-medium">
                {comment?.user?.username || t("commentSection.anonymous")}
              </h4>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm text-gray-600">
                  {comment?.created_at?.slice(0, 10) || "N/A"}
                </span>
              </div>
            </div>
          </div>
          <p className="mt-2 text-gray-600">
            {comment?.comment || t("commentSection.noComment")}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
  const i18nextLng = window.localStorage.getItem("i18nextLng");

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex justify-center gap-2 mt-6"
    >
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="px-3 py-1 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {i18nextLng === "ar" ? (
          <ChevronRight className="w-4 h-4" />
        ) : (
          <ChevronLeft className="w-4 h-4" />
        )}{" "}
      </motion.button>

      {pages.map((page) => (
        <motion.button
          key={page}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => onPageChange(page)}
          className={`px-3 py-1 rounded-lg ${
            currentPage === page
              ? "bg-emerald-500 text-white"
              : "bg-gray-100 hover:bg-gray-200"
          }`}
        >
          {page}
        </motion.button>
      ))}

      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="px-3 py-1 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {i18nextLng === "ar" ? (
          <ChevronLeft className="w-4 h-4" />
        ) : (
          <ChevronRight className="w-4 h-4" />
        )}
      </motion.button>
    </motion.div>
  );
};

const DesktopDetails = ({ productDetails, products }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [imageError, setImageError] = useState({});
  const { selectedCurrency, convertPrice } = useCurrency();
  const commentsPerPage = 3;
  const { t } = useTranslation();
  const [loading, setloading] = useState(false);

  // Reset state when product changes
  useEffect(() => {
    setCurrentImageIndex(0);
    setImageError({});
    setCurrentPage(1);
  }, [productDetails?.id]);

  const handleImageError = (index) => {
    setImageError((prev) => ({ ...prev, [index]: true }));
  };

  const hasImages = productDetails?.images && productDetails.images.length > 0;

  const { addToWishList, deleteFromWhisList, wishlist } = useWishlist();
  const wishlistData = wishlist;

  const isInWishlist =
    Array.isArray(wishlistData) &&
    wishlist.some((item) => item.id === productDetails?.id);

  const handleWishlist = async () => {
    try {
      setloading(true);
      if (isInWishlist) {
        await deleteFromWhisList(productDetails?.id);
      } else {
        await addToWishList(productDetails?.id);
      }
    } finally {
      setloading(false);
    }
  };

  const similarProducts =
    products?.filter(
      (item) =>
        item?.category?.main?.name === productDetails?.category?.main?.name &&
        item.id !== productDetails?.id
    ) || [];

  const totalPages = Math.ceil(
    (productDetails?.comments?.length || 0) / commentsPerPage
  );

  const paginatedComments =
    productDetails?.comments?.slice(
      (currentPage - 1) * commentsPerPage,
      currentPage * commentsPerPage
    ) || [];

  const nextImage = () => {
    if (!hasImages) return;
    setCurrentImageIndex((prev) =>
      prev === productDetails.images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    if (!hasImages) return;
    setCurrentImageIndex((prev) =>
      prev === 0 ? productDetails.images.length - 1 : prev - 1
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="w-[90%] mx-auto px-4 py-8"
    >
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16"
      >
        <motion.div
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          className="relative group"
        >
          <div
            className="relative h-[500px] overflow-hidden rounded-lg bg-gray-100"
            onMouseEnter={() => setIsZoomed(true)}
            onMouseLeave={() => setIsZoomed(false)}
          >
            {!hasImages || imageError[currentImageIndex] ? (
              <DefaultImage className="w-full h-full" />
            ) : (
              <motion.img
                src={productDetails.images[currentImageIndex].image?.startsWith('/') 
                  ? productDetails.images[currentImageIndex].image
                  : `${process.env.NEXT_PUBLIC_BASE_URL}${productDetails.images[currentImageIndex].image}`}
                alt={`Product view ${currentImageIndex + 1}`}
                className="w-full h-full object-cover"
                onError={() => handleImageError(currentImageIndex)}
                animate={{
                  scale: isZoomed ? 1.1 : 1,
                }}
                transition={{ duration: 0.5 }}
              />
            )}

            {hasImages && productDetails.images.length > 1 && (
              <>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={prevImage}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                >
                  <ChevronLeft className="w-6 h-6" />
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={nextImage}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                >
                  <ChevronRight className="w-6 h-6" />
                </motion.button>
              </>
            )}
          </div>

          {hasImages && productDetails.images.length > 1 && (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="flex gap-2 mt-4"
            >
              {productDetails.images.map((img, index) => (
                <motion.button
                  key={index}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`w-16 h-16 rounded-lg overflow-hidden transition-transform duration-200 ${
                    currentImageIndex === index ? "ring-2 ring-emerald-500" : ""
                  }`}
                >
                  {imageError[index] ? (
                    <DefaultImage className="w-full h-full" />
                  ) : (
                    <img
                      src={img.image?.startsWith('/') 
                        ? img.image
                        : `${process.env.NEXT_PUBLIC_BASE_URL}${img.image}`}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={() => handleImageError(index)}
                    />
                  )}
                </motion.button>
              ))}
            </motion.div>
          )}
        </motion.div>

        <motion.div
          initial={{ x: 20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="space-y-6"
        >
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="space-y-2"
          >
            <div className="flex items-center justify-between ">
              <h1 className="text-3xl mb-4 font-bold text-gray-900">
                {productDetails?.name || t("productDetails.noName")}
              </h1>
              <h1 className="text-3xl  font-bold text-gray-900">
                {productDetails?.government || t("productDetails.noName")}
              </h1>
            </div>
            <motion.p
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="text-2xl font-semibold text-emerald-500"
            >
              {productDetails?.price
                ? `${convertPrice(productDetails.price)} - ${
                    selectedCurrency.code
                  }`
                : t("productDetails.noPrice")}
            </motion.p>
          </motion.div>

          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-gray-600 leading-relaxed"
          >
            {productDetails?.description || t("productDetails.noDescription")}
          </motion.p>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="bg-gray-50 p-4 rounded-lg space-y-2"
          >
            <div className="flex items-center gap-2">
              <Store className="w-5 h-5 text-gray-600" />
              <span className="font-medium">
                {productDetails?.user?.username ||
                  t("productDetails.anonymousSeller")}
              </span>
            </div>
            <motion.button
              whileHover={{ x: 5 }}
              className="text-emerald-500 text-sm font-medium transition-colors"
            >
              <Link href={`/Desktop/products/saller-info/${productDetails?.id}`}>
                {t("productDetails.viewSeller")}
              </Link>
            </motion.button>
          </motion.div>
          <motion.button
            onClick={handleWishlist}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full bg-emerald-500 text-white py-3 px-6 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors hover:bg-emerald-600"
          >
            {loading ? (
              <>
                <LoadingSpinner />
              </>
            ) : (
              <>
               <Heart
                      className={`w-5 h-5 ${
                        isInWishlist
                          ? "text-red-600 fill-red-600"
                          : "text-white"
                      }`}
                    />
                {isInWishlist ? (
                  <>{t("productDetails.removeFromWishlist")}</>
                ) : (
                  <>{t("productDetails.addToWishlist")}</>
                )}
              </>
            )}
          </motion.button>
          {/* <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full bg-emerald-500 text-white py-3 px-6 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors hover:bg-emerald-600"
          >
            <ShoppingCart className="w-5 h-5" />
            {t("productDetails.addToCart")}
          </motion.button> */}
        </motion.div>
      </motion.div> 

      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.7 }}
        className="mt-16"
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {t("productDetails.customerReviews", {
              count: productDetails?.comments?.length || 0,
            })}
          </h2>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 bg-emerald-500 text-white px-4 py-2 rounded-lg transition-colors hover:bg-emerald-600"
          >
            <MessageCircle className="w-5 h-5" />
            {t("productDetails.writeReview")}
          </motion.button>
        </div>

        <AnimatePresence>
          {!paginatedComments || paginatedComments.length === 0 ? (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-gray-600 text-center py-8"
            >
              {t("productDetails.noReviews")}
            </motion.p>
          ) : (
            <motion.div className="space-y-6">
              {paginatedComments.map((comment, index) => (
                <motion.div
                  key={comment.id || index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Comment comment={comment} />
                </motion.div>
              ))}
              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <CommentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        productId={productDetails?.id}
      />

      {similarProducts.length > 0 && (
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-16"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            {t("productDetails.similarProducts")}
          </h2>

          <Swiper
            modules={[Autoplay, Navigation, SwiperPagination]}
            spaceBetween={24}
            slidesPerView={1}
            autoplay={{
              delay: 3000,
              disableOnInteraction: false,
            }}
            breakpoints={{
              640: {
                slidesPerView: 2,
              },
              768: {
                slidesPerView: 3,
              },
              1024: {
                slidesPerView: 4,
              },
            }}
            className="similar-products-swiper"
          >
            {similarProducts.map((product) => (
              <SwiperSlide key={product.id} className="my-10">
                <motion.div
                  whileHover={{ y: -10 }}
                  className="group cursor-pointer"
                >
                  <Link
                    href={`/Desktop/products/${product.id}`}
                    className="block"
                  >
                    <div className="relative overflow-hidden rounded-lg mb-3">
                      {!product?.images?.[0] ||
                      imageError[`similar-${product.id}`] ? (
                        <DefaultImage className="w-full h-64" />
                      ) : (
                        <img
                          src={product.images[0].image?.startsWith('/')
                            ? product.images[0].image
                            : `${process.env.NEXT_PUBLIC_BASE_URL}${product.images[0].image}`}
                          alt={product.name || "Product"}
                          className="w-full h-64 object-cover rounded-md transition-transform duration-300 group-hover:scale-110"
                          onError={() =>
                            handleImageError(`similar-${product.id}`)
                          }
                        />
                      )}
                    </div>
                    <h3 className="font-medium text-gray-900 mb-1 truncate">
                      {product?.name || t("productDetails.unnamedProduct")}
                    </h3>
                    <div className="flex justify-between items-center">
                      <span className="text-emerald-500 font-semibold">
                        {product?.price
                          ? `${convertPrice(product.price)} - ${
                              selectedCurrency.code
                            }`
                          : t("productDetails.noPrice")}
                      </span>
                      <span className="text-gray-500 text-sm">
                        {product?.government || t("productDetails.locationNA")}
                      </span>
                    </div>
                  </Link>
                </motion.div>
              </SwiperSlide>
            ))}
          </Swiper>
        </motion.div>
      )}
    </motion.div>
  );
};

export default DesktopDetails;
